import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { WaiverConsentCard, isWaiverConsentComplete } from '@butter/ui/domains/legal';
import { queryKeys } from '~/lib/query/keys';
import { IS_PRODUCTION } from '~/lib/apiClient/constants';
import { signWaiver, downloadWaiverDocument } from '~/services/internalApi/waiver';
import { downloadBlob } from '@butter/ui/utils';
import type { WaiverStanding } from '~/services/internalApi/waiver';
import type { WaiverSignaturePaths } from '@lgc/database/types';

/**
 * "Review & Sign Waiver" — shown when a signed-in member owes their club a
 * signature.
 *
 * No back affordance and no dismiss. This is the club's condition for using the
 * portal, and the gate re-renders it on the next visit anyway; a way out would
 * only produce a loop.
 *
 * @module features/waivers/SignWaiversScreen
 */
interface SignWaiversScreenProps {
  waivers: WaiverStanding[];
  clubId: number;
}

export function SignWaiversScreen({ waivers, clubId }: SignWaiversScreenProps) {
  const queryClient = useQueryClient();
  const [signatures, setSignatures] = useState<Record<number, WaiverSignaturePaths | null>>({});
  const [agreements, setAgreements] = useState<Record<number, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canContinue = waivers.every((waiver) =>
    isWaiverConsentComplete({
      requireSignature: waiver.requireSignature,
      requireAgreement: waiver.requireAgreement,
      signature: signatures[waiver.waiverVersionId] ?? null,
      agreed: agreements[waiver.waiverVersionId] ?? false,
    }),
  );

  /**
   * Post every signature, then refetch standing so the gate re-evaluates.
   *
   * Sequential rather than `Promise.all`: standing is an array and a rejection
   * would hide which entry failed. A club runs one active waiver today, so this
   * writes a single compliance row — the loop is what keeps that a fact about
   * the data rather than an assumption baked into the submit path. A failure
   * keeps the member here with the error rather than letting them into the
   * portal unsigned.
   */
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      for (const waiver of waivers) {
        await signWaiver({
          clubId,
          waiverVersionId: waiver.waiverVersionId,
          agreementAccepted: agreements[waiver.waiverVersionId] ?? false,
          signaturePaths: signatures[waiver.waiverVersionId] ?? null,
        });
      }
    } catch (err) {
      if (!IS_PRODUCTION) {
        console.error('[SignWaiversScreen] Waiver signing failed:', err);
      }
      setError(err instanceof Error ? err.message : 'Failed to record your signature. Please try again.');
    } finally {
      // Invalidate whatever happened. On success this is what lets the member
      // through the gate; on a partial failure — waiver 2 of 3 rejected — it is
      // what makes the UI reflect the rows that *were* written, instead of
      // showing all three as outstanding until the next refetch.
      await queryClient.invalidateQueries({ queryKey: queryKeys.waiver.standing(clubId) });
      // Must reset even on success — the gate re-renders this same component
      // when standing still has something pending, so unmount is not guaranteed.
      setIsSubmitting(false);
    }
  };

  const handleDownload = async (waiverVersionId: number) => {
    try {
      downloadBlob(await downloadWaiverDocument(clubId, waiverVersionId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download the waiver.');
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-6 py-10">
      <h1 className="text-3xl font-semibold text-butter-gray-900">Review &amp; Sign Waiver</h1>
      <p className="mt-2 text-butter-gray-500">
        {waivers.length > 1
          ? 'Your club requires these waivers before you continue.'
          : 'Your club requires this waiver before you continue.'}
      </p>

      <div className="mt-8 space-y-10">
        {waivers.map((waiver) => (
          <WaiverConsentCard
            key={waiver.waiverVersionId}
            title={waiver.title}
            contentHtml={waiver.content}
            requireSignature={waiver.requireSignature}
            requireAgreement={waiver.requireAgreement}
            agreementText={waiver.agreementText}
            signature={signatures[waiver.waiverVersionId] ?? null}
            onSignatureChange={(value) =>
              setSignatures((previous) => ({ ...previous, [waiver.waiverVersionId]: value }))
            }
            agreed={agreements[waiver.waiverVersionId] ?? false}
            onAgreedChange={(value) =>
              setAgreements((previous) => ({ ...previous, [waiver.waiverVersionId]: value }))
            }
            onDownloadPdf={() => handleDownload(waiver.waiverVersionId)}
            disabled={isSubmitting}
            data-testid={`waiver-gate-consent-${waiver.waiverVersionId}`}
          />
        ))}
      </div>

      {error && <p className="mt-4 text-sm text-butter-error-500">{error}</p>}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canContinue || isSubmitting}
        className="mt-8 w-full rounded-full bg-theme-600 px-6 py-4 text-lg font-semibold text-white shadow-md transition-colors text-interactive:bg-theme-700 disabled:cursor-not-allowed disabled:bg-gray-400"
        data-testid="waiver-gate-submit"
      >
        {isSubmitting ? 'Signing…' : 'Sign & Continue'}
      </button>
    </div>
  );
}
